import "leaflet/dist/leaflet.css";

import {
  map,
  tileLayer,
  marker,
  control,
  geoJSON,
  layerGroup,
  icon,
} from "leaflet";
import "./L.Control.Zoomslider";
import proj4 from "proj4";
import mediumZoom from "./medium-zoom";
import { createPopupContent } from "./popup";

import settings from "../public/settings.json";

const gsi_layers = [
  {
    id: 100,
    zindex: 0,
    template: "https://cyberjapandata.gsi.go.jp/xyz/std/{z}/{x}/{y}.png",
    minzoom: 12,
    maxzoom: 17,
    category1: "地図・絵図",
    category2: "地理院地図",
    layer_name: "地理院標準地図",
    type: "Base",
    target: "all",
  },
  {
    id: 101,
    zindex: 0,
    template:
      "https://cyberjapandata.gsi.go.jp/xyz/seamlessphoto/{z}/{x}/{y}.jpg",
    minzoom: 12,
    maxzoom: 17,
    category1: "地図・絵図",
    category2: "地理院地図",
    layer_name: "オルソ航空写真",
    type: "Base",
    target: "all",
  },
  {
    id: 102,
    zindex: 0,
    template: "https://cyberjapandata.gsi.go.jp/xyz/ort_old10/{z}/{x}/{y}.png",
    minzoom: 12,
    maxzoom: 17,
    category1: "地図・絵図",
    category2: "地理院地図",
    layer_name: "1960年代航空写真",
    type: "Base",
    target: "all",
  },
];
const gsi_attr = "&copy; 地理院地図";
let onOffTooltip;

export default async function initializer(areaJson) {
  let cbStore;
  const settings = await initFileLoader(areaJson, (cb_data) => {
    if (cbStore) {
      cbStore(cb_data);
    }
  });
  const overlays = [];

  const mymap = map("map", {
    minZoom: settings.min_zoom,
    maxZoom: settings.max_zoom,
    maxBounds: settings.bounds.map((point) => point.reverse()),
    zoomControl: false,
    zoomsliderControl: true,
  }).setView(settings.center.reverse(), settings.home_zoom);

  mymap.createPane("polyPane").style.zIndex = 200;
  mymap.createPane("linePane").style.zIndex = 300;
  mymap.createPane("poiPane").style.zIndex = 400;
  onOffTooltip = (layers) => {
    const layer = layers[0];
    const parent = layers[1].layer;
    const zoom = mymap.getZoom();
    if (zoom > 16) {
      layer.openTooltip();
      parent.closeTooltip();
    } else {
      layer.closeTooltip();
      parent.openTooltip();
    }
  };
  mymap.on("zoomend", function () {
    setTimeout(() => {
      settings.on_off_tooltips.forEach(onOffTooltip);
    }, 1);
  });

  control
    .scale({
      position: "bottomleft",
      imperial: false,
      maxWidth: 300,
    })
    .addTo(mymap);

  for (let z = settings.min_zoom; z <= settings.max_zoom; z++) {
    proj4.defs([
      [
        `ZOOMPIX:${z}`,
        `+proj=merc +a=${Math.pow(2, z + 7) / Math.PI} +b=${
          Math.pow(2, z + 7) / Math.PI
        } +lat_ts=0.0 +lon_0=0.0 +x_0=${Math.pow(2, z + 7)} +y_0=-${Math.pow(
          2,
          z + 7
        )} +k=1.0 +axis=esu +units=m +nadgrids=@null +no_defs`,
      ],
    ]);
  }

  const pan = (dx, dy) => {
    const center = mymap.getCenter();
    const zoom = mymap.getZoom();
    const zoomPix = `ZOOMPIX:${zoom}`;
    const pixel = proj4("EPSG:4326", zoomPix, [center.lng, center.lat]);
    pixel[0] += dx * settings.pan_delta;
    pixel[1] -= dy * settings.pan_delta;
    const newll = proj4(zoomPix, "EPSG:4326", pixel);
    mymap.panTo(newll.reverse());
  };

  return {
    settings,
    getBaseMaps() {
      return settings.bases.map((base) => {
        return {
          id: base.id,
          category1: base.category1,
          category2: base.category2,
          layer_name: base.layer_name,
          selected: mymap.hasLayer(base.layer),
        };
      });
    },
    setBaseMap(id) {
      settings.bases.forEach((base) => {
        if (base.id === id) {
          base.layer.addTo(mymap);
        } else {
          base.layer.removeFrom(mymap);
        }
      });
    },
    getOverlayMaps() {
      return settings.overlays.map((base) => {
        return {
          id: base.id,
          category1: base.category1,
          category2: base.category2,
          layer_name: base.layer_name,
          selected: overlays.indexOf(base) > -1,
        };
      });
    },
    toggleOverlay(id) {
      settings.overlays.forEach((overlay) => {
        if (id === overlay.id) {
          const array_id = overlays.indexOf(overlay);
          if (array_id > -1) {
            delete overlays[array_id];
            overlay.layer.removeFrom(mymap);
          } else {
            overlays.push(overlay);
            overlay.layer.addTo(mymap);
          }
        }
      });
    },
    getVectorLayers() {
      return settings.vectors.map((vector) => {
        return {
          id: vector.id,
          category1: vector.category1,
          category2: vector.category2,
          layer_name: vector.layer_name,
          selected: mymap.hasLayer(vector.layer),
        };
      });
    },
    getVectorLayerById(id) {
      return this.getVectorLayers().find(({ id: vId }) => vId === id);
    },
    getVectorLayersByCategory1(category1) {
      return this.getVectorLayers().filter(
        ({ category1: vCategory1 }) => vCategory1 === category1
      );
    },
    showVectorLayerById(id) {
      const vector = settings.vectors.find(({ id: vId }) => vId === id);
      if (vector) {
        vector.layer.addTo(mymap);
      }
    },
    hiddenVectorLayerById(id) {
      const vector = settings.vectors.find(({ id: vId }) => vId === id);
      if (vector) {
        vector.layer.removeFrom(mymap);
      }
    },
    isShowLayerById(id) {
      const vector = settings.vectors.find(({ id: vId }) => vId === id);
      return mymap.hasLayer(vector.layer);
    },
    toggleVectorLayers(id) {
      const vector = settings.vectors.find((vector) => vector.id === id);
      if (this.isShowLayerById(vector.id)) {
        vector.layer.removeFrom(mymap);
      } else {
        vector.layer.addTo(mymap);
      }
    },
    onClick(cb) {
      cbStore = cb;
    },
    offClick() {
      cbStore = undefined;
    },
    panNorth() {
      pan(0, -1);
    },
    panSouth() {
      pan(0, 1);
    },
    panWest() {
      pan(-1, 0);
    },
    panEast() {
      pan(1, 0);
    },
    zoomIn() {
      mymap.setZoom(mymap.getZoom() + 1);
    },
    zoomOut() {
      mymap.setZoom(mymap.getZoom() - 1);
    },
    setZoom(zoom) {
      mymap.setZoom(zoom);
    },
    getZoom() {
      return mymap.getZoom();
    },
    getCenter() {
      const ll = mymap.getCenter();
      return [ll.lng, ll.lat];
    },
    setCenter(lnglat) {
      mymap.panTo(lnglat.reverse());
    },
    home() {
      mymap.panTo(settings.center.reverse());
      mymap.setZoom(settings.home_zoom);
    },
  };
}

async function initFileLoader(area, callback) {
  const base = window.location.href;
  // const r = await fetch(`./${area}.json`).then((r) => r.json());
  // const settings = await fetch("./settings.json").then((r) => r.json());
  const styles = settings.styles;
  area.pan_delta = area.pan_delta ?? settings.pan_delta ?? 100;
  area.home_zoom = area.home_zoom ?? settings.home_zoom ?? 13;
  area.max_zoom = area.max_zoom ?? settings.max_zoom ?? 17;
  area.min_zoom = area.min_zoom ?? settings.min_zoom ?? 12;
  area.layers = area.layers.concat(gsi_layers);
  area.bases = [];
  area.overlays = [];
  area.vectors = [];
  area.on_off_tooltips = [];

  const zoom = mediumZoom({
    background: "rgba(0, 0, 0, 0.7)",
  });

  const extractor = async (feat) => {
    if (feat.resources) {
      feat.resources = await Promise.all(feat.resources.map(extractor));
      feat.layer = layerGroup(feat.resources.map((res) => res.layer));
    } else if (feat.type === "Base" || feat.type === "Overlay") {
      const template = feat.template.match(/^http/)
        ? feat.template
        : `${settings.contents_root || ""}${feat.template}`;
      feat.layer = tileLayer(decodeURI(new URL(template, base).href), {
        attribution: gsi_attr,
      });
      feat.layer.setZIndex(feat.zindex);
    } else {
      // feat.geojson = await fetch(feat.file).then((r) => r.json());
      feat.geojson = feat.data;
      const options = {
        pane:
          feat.type === "Polygon"
            ? "polyPane"
            : feat.type === "LineString"
            ? "linePane"
            : "poiPane",
      };
      if (feat.style) {
        const style = Object.assign(
          {},
          typeof feat.style === "string" ? styles[feat.style] : feat.style
        );
        style.iconUrl = new URL(style.iconUrl, base).href;
        if (feat.type === "Point") {
          options.pointToLayer = (geoJsonPoint, latlng) => {
            if (geoJsonPoint.properties.style) {
              const individualStyle = Object.assign(
                {},
                typeof geoJsonPoint.properties.style === "string"
                  ? styles[geoJsonPoint.properties.style]
                  : geoJsonPoint.properties.style
              );
              individualStyle.iconUrl = new URL(
                individualStyle.iconUrl,
                base
              ).href;
              return marker(latlng, { icon: icon(individualStyle) });
            } else {
              return marker(latlng, { icon: icon(style) });
            }
          };
        } else {
          options.style = style;
        }
      }

      options.onEachFeature = (feature, layer) => {
        if (feat.type !== "Point" && feature.properties.style) {
          const style = Object.assign(
            {},
            typeof feature.properties.style === "string"
              ? styles[feature.properties.style]
              : feature.properties.style
          );
          layer.setStyle(style);
        }

        if (feature.properties.content) {
          const props = Object.assign(
            {
              category1: feat.category1,
              category2: feat.category2,
              layer_name: feat.layer_name,
              type: feat.type,
            },
            feature.properties
          );
          if (props.content) {
            props.content = `${settings.contents_root || ""}${props.content}`;
          }
          layer.on({
            click: (e) => {
              const props_ = Object.assign({}, props);
              props_.lnglat = [e.latlng.lng, e.latlng.lat];
              props_.layer = layer;
              callback(props);
            },
          });

          const { contents, play, pause } = createPopupContent(props);

          layer.bindPopup(contents, {
            className: "popup-wrapper",
            maxWidth: "100%",
          });

          layer.getPopup().on("add", () => {
            // wait for dom created
            setTimeout(() => {
              zoom.attach(document.querySelectorAll("[data-zoomable]"));
              pause();
            }, 1);
          });

          layer.getPopup().on("remove", () => {
            zoom.detach(document.querySelectorAll("[data-zoomable]"));
          });

          layer.openPopup();

          zoom.on("open", () => {
            play();
          });
          zoom.on("close", () => {
            pause();
          });
        }
        if (feature.properties.name) {
          layer.bindTooltip(feature.properties.name, {
            permanent: true,
          });
          if (feat.less_zoom_name) {
            area.on_off_tooltips.push([layer, feat]);
            layer.on("add", () => {
              onOffTooltip([layer, feat]);
            });
          } else {
            layer.openTooltip();
          }
        }
      };
      feat.layer = geoJSON(feat.geojson, options);
      if (feat.less_zoom_name) {
        feat.layer.bindTooltip(feat.less_zoom_name, {
          permanent: true,
        });
      }
    }
    return feat;
  };
  (await Promise.all(area.layers.map(extractor))).forEach((layer) => {
    if (layer.type === "Base") area.bases.push(layer);
    else if (layer.type === "Overlay") area.overlays.push(layer);
    else area.vectors.push(layer);
  });
  return area;
}
